import ModalStack from '-/Custom/ModalStack';
import { useLocalSearchParams } from 'expo-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useClient } from 'urql';
import { graphql, readFragment } from '@/gql/graphql';
import { TeamSelectItemFragment } from '../Team/TeamSelectItem';
import type { EventGenerateFormValues } from './EventGenerate.types';
import { createEventGenerateInitialGames } from './EventGenerate.utils';

const GetEventGenerateFormValues = graphql(/* GraphQL */ `
  query GetEventGenerateFormValues($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      id
      rootParentId: root_parent_id
      event_teams(order_by: { team: { title: asc } }) {
        event_id
        team_id
        team {
          ...TeamSelectItem
        }
      }
    }
  }
`, [TeamSelectItemFragment]);

const GetEventGenerateRootTeams = graphql(/* GraphQL */ `
  query GetEventGenerateRootTeams($rootEventId: uuid!) {
    team(
      where: { root_event_id: { _eq: $rootEventId } }
      order_by: { title: asc }
    ) {
      ...TeamSelectItem
    }
  }
`, [TeamSelectItemFragment]);

function getDefaultEventGenerateFormValues(): EventGenerateFormValues {
  return {
    selectedTeams: [],
    initialGames: [],
    repeatEvery: {
      weeks: 1,
    },
    matchupCount: 1,
    games: [],
  };
}

export default function EventGenerateLayout() {
  const client = useClient();
  const { eventId } = useLocalSearchParams<{ eventId: string }>();

  const form = useForm<EventGenerateFormValues>({
    defaultValues: async () => {
      const { data } = await client.query(GetEventGenerateFormValues, {
        eventId,
      });
      const selectedTeams =
        data?.event_by_pk?.event_teams.map((team) => {
          const teamFragment = readFragment(TeamSelectItemFragment, team.team);

          return teamFragment;
        }) || [];
      const rootParentId = data?.event_by_pk?.rootParentId;
      const rootTeams =
        selectedTeams.length || !rootParentId
          ? []
          : (
              await client.query(GetEventGenerateRootTeams, {
                rootEventId: rootParentId,
              })
            ).data?.team || [];
      const fallbackTeams = readFragment(TeamSelectItemFragment, rootTeams).map(
        (team) => ({ ...team }),
      );
      const teamSelection = selectedTeams.length
        ? selectedTeams
        : fallbackTeams;

      return {
        ...getDefaultEventGenerateFormValues(),
        selectedTeams: teamSelection,
        initialGames: createEventGenerateInitialGames(teamSelection.length),
      };
    },
  });

  return (
    <FormProvider {...form}>
      <ModalStack />
    </FormProvider>
  );
}
